/**
 * The step's drill configuration, read from the loaded Step (design §2):
 * step type, drill, settings, QA options, display method and the resolved
 * drill kind's strategy. Read-only — no effects, no watchers.
 */
import { computed, type Ref } from 'vue';
import type { Step } from '../models/Step';
import type {
  DrillDrillType,
  DrillQAOptions,
  DrillQASettings,
} from 'src/types/course/drill';
import type {
  StepTypeComponnet,
  StepTypeKebabCase,
  StepTypeCamelCase,
} from 'types/course/step';
import {
  componentNameCamelCase,
  componentNameKebabCase,
} from 'shared/composables/strapi/useComponent';
import {
  normalizeDisplayMethod,
  type NormalizedDisplayMethod,
} from '../utilities/drillDisplay';
import { isDrillKind } from '../utilities/drillStrategies/resolveDrillKind';
import { drillContextOf, getDrillStrategy } from '../utilities/drillStrategies';

export interface UseDrillConfigOptions {
  /** The loaded step (useStepData's `documentStepRepo`). */
  documentStepRepo: Readonly<Ref<Step | null | undefined>>;
}

export function useDrillConfig({ documentStepRepo }: UseDrillConfigOptions) {
  const stepType0 = computed(() => {
    return documentStepRepo.value?.step_type?.[0];
  });

  const stepName = computed(() => {
    return documentStepRepo.value?.step_name || '';
  });

  const stepDrill = computed(() => {
    return stepType0.value?.drill;
  });

  const drillSettings = computed(() => {
    return stepDrill.value?.settings;
  });

  const filterSettings = computed(() => {
    return stepDrill.value?.filter_settings;
  });

  const stepTypeCamelCase = computed(() => {
    if (!stepTypeComponent.value) return null;
    return componentNameCamelCase(stepTypeComponent.value) as StepTypeCamelCase;
  });

  const stepTypeComponent = computed<StepTypeComponnet | null>(() => {
    if (!documentStepRepo.value) return null;
    const { step_type } = documentStepRepo.value;
    if (!step_type) return null;
    return (step_type?.[0]?.__component as StepTypeComponnet) ?? null;
  });

  const stepType = computed<StepTypeKebabCase | null>(() => {
    if (!stepTypeComponent.value) return null;
    return componentNameKebabCase(stepTypeComponent.value);
  });

  const drillType = computed(() => {
    return stepDrill.value?.drill_type?.[0] as DrillDrillType;
  });

  const drillQaOptions = computed(() => {
    return drillType.value?.qa_options as DrillQAOptions;
  });

  const drillQaSettings = computed(() => {
    return drillType.value?.qa_settings as unknown as DrillQASettings;
  });

  const questionDisplayMethod = computed<NormalizedDisplayMethod>(() => {
    const normalizedFromSettings = normalizeDisplayMethod(
      drillQaSettings.value?.source_display_method,
    );
    if (normalizedFromSettings) return normalizedFromSettings;

    const normalizedFromOptions = normalizeDisplayMethod(
      drillQaOptions.value?.question_display_settings,
    );
    if (normalizedFromOptions) return normalizedFromOptions;

    return 'Reading';
  });

  const isQuestionHearingEnabled = computed(() => {
    return questionDisplayMethod.value !== 'Reading';
  });

  const isQuestionReadingEnabled = computed(() => {
    return questionDisplayMethod.value !== 'Hearing';
  });

  const filterStepType = computed(() =>
    stepTypeCamelCase.value === 'Filter'
      ? documentStepRepo.value?.typePascalCase
      : stepTypeCamelCase.value,
  );

  // The resolved drill kind's strategy (Theory/unknown → null).
  const drillKind = computed(() =>
    isDrillKind(filterStepType.value) ? filterStepType.value : null,
  );
  const strategy = computed(() => getDrillStrategy(drillKind.value));
  const drillContext = computed(() => drillContextOf(drillType.value));

  return {
    stepType0,
    stepName,
    stepDrill,
    drillSettings,
    filterSettings,
    stepTypeComponent,
    stepType,
    stepTypeCamelCase,
    filterStepType,
    drillKind,
    strategy,
    drillType,
    drillQaOptions,
    drillQaSettings,
    questionDisplayMethod,
    isQuestionHearingEnabled,
    isQuestionReadingEnabled,
    drillContext,
  };
}

export type UseDrillConfigReturn = ReturnType<typeof useDrillConfig>;
